import React, { useState, useEffect } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// 1. Import Global Settings
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  // Lead time labels + colors
  QS_DAYS, RUSH_DAYS,
  QS_TIMELINE_TEXT, RUSH_TIMELINE_TEXT,
  QS_LABEL, QS_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

// 2. Import Data
import { holsterData } from '../data/cradleData'; 
import '../App.css';

function CradleBuilder() {
  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [handId, setHandId] = useState("right");
  const [brandId, setBrandId] = useState(""); 
  const [gunId, setGunId] = useState("");
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  const [breakthroughId, setBreakthroughId] = useState("");
  const [warrantyId, setWarrantyId] = useState("");
  
  // Rush Service (Even if Quick Ship, we keep the state for consistency)
  const [rushService, setRushService] = useState(false);

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL,
    generateShareableURL,
    saveToLocalStorage,
    loadFromLocalStorage,
    savedConfigs,
    saveNamedConfig,
    deleteNamedConfig,
    copyToClipboard,
    copySuccess,
    shareModalOpen,
    setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, brandId, gunId, colorId, mountId, breakthroughId, warrantyId, rushService
  });

  const loadConfig = (config) => {
    if (!config) return;
    setHandId(config.handId || 'right');
    setBrandId(config.brandId || '');
    setGunId(config.gunId || '');
    setColorId(config.colorId || '');
    setMountId(config.mountId || '');
    setBreakthroughId(config.breakthroughId || '');
    setWarrantyId(config.warrantyId || '');
    setRushService(config.rushService || false);
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [handId, brandId, gunId, colorId, mountId, breakthroughId, warrantyId, rushService]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- FILTERING ---
  const availableGuns = brandId 
    ? holsterData.guns.filter(g => g.brand_id === brandId)
    : [];

  // --- HELPERS ---
  const selectedGun = holsterData.guns.find(g => g.id === gunId);
  const selectedColor = holsterData.colors.find(c => c.id === colorId);
  const selectedMount = holsterData.mounts.find(m => m.id === mountId);
  const selectedBreakthrough = holsterData.breakthrough.find(b => b.id === breakthroughId);
  const selectedWarranty = holsterData.warranties.find(w => w.id === warrantyId);

  // --- PRICING ---
  const BASE_PRICE = 79.99;
  
  const subtotal = 
    BASE_PRICE +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedBreakthrough?.price || 0) +
    (selectedWarranty?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  // --- LEAD TIME (CRADLE USES THE GLOBAL FASTER PROGRAM) ---
  const calculateLeadTime = () => {
    if (!selectedGun) return { type: "PENDING", text: "Select Options" };
    
    const targetDays = (rushService && ENABLE_RUSH_SHIPPING) ? RUSH_DAYS : QS_DAYS;
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    // If Global Rush is enabled, we allow them to pay for Priority handling
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return {
            type: "RUSH PRIORITY",
            timeline: RUSH_TIMELINE_TEXT,
            date: dateString,
            color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR),
            isCustom: true // Allows toggle
        };
    }

    return {
      type: QS_LABEL,
      timeline: QS_TIMELINE_TEXT,
      date: dateString,
      color: QS_COLOR, bg: leadTimeTint(QS_COLOR), // Green
      isCustom: false // Hides Rush toggle (Change to true if you want to offer Rush on Cradle)
    };
  };

  const leadTimeInfo = calculateLeadTime();
  
  const isReady = brandId && gunId && colorId && mountId && breakthroughId && warrantyId;

  // --- ADD TO CART FUNCTION ---
  const handleAddToCart = () => {
    if (!isReady) return;

    // 1. Build the Configuration Text
    const configuration = [
        `Gun Model: ${selectedGun?.name}`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        `Color: ${selectedColor?.name}`,
        `Clip Size: ${selectedMount?.name}`, // Specific to Cradle
        optionLine("Breakthrough Clean", selectedBreakthrough?.name),
        `Warranty: ${selectedWarranty?.name}`,
        rushService ? `RUSH SERVICE: YES (+$${RUSH_PRICE})` : 'Rush: No',
    ].filter(Boolean).join('\n');

    // 2. Prepare URL Params
    // REPLACE 66666 WITH THE REAL CRADLE PRODUCT ID
    const productID = 66666; 
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('attribute_configuration', configuration);
    params.append('custom_price', total);

    const addToCartURL = `/checkout/?${params.toString()}`;

    // 3. Log & Redirect
    console.log("--- ADDING TO CART ---");
    console.log("Config:", configuration);
    console.log("Total:", total);
    
    // UNCOMMENT TO GO LIVE
    // window.location.href = addToCartURL;
    
    alert(`Simulating Add to Cart!\n\nPrice: $${total}\n\nConfig:\n${configuration}`);
  };

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">The Cradle<br/><span style={{fontSize:"16px", color:"#D5A678"}}>APPENDIX WITH CLAW FOR SUPPRESSORS</span></h1>
        
        {/* FEATURES */}
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>Suppressor Compatible:</strong> Designed for carry with attached suppressor.</li>
             <li><strong style={{ color: "#fff" }}>Ambidextrous Design:</strong> Usable by right and left-handed shooters.</li>
             <li><strong style={{ color: "#fff" }}>Minimalist Profile:</strong> Reduces bulk for discreet carry.</li>
             <li><strong style={{ color: "#fff" }}>Concealment Claw:</strong> Minimizes printing effectively.</li>
             <li><strong style={{ color: "#fff" }}>Full Trigger Guard Protection:</strong> Ensures safe carry.</li>
           </ul>

           {/* LEAD TIME BOX */}
           {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>

                    {/* RUSH TOGGLE */}
                    {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                        <div style={{ marginLeft: "15px" }}>
                            <label style={{ 
                                display: "flex", alignItems: "center", cursor: "pointer",
                                background: rushService ? "#D5A678" : "transparent",
                                border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px",
                                transition: "all 0.2s"
                            }}>
                                <input 
                                    type="checkbox" 
                                    checked={rushService} 
                                    onChange={(e) => setRushService(e.target.checked)}
                                    style={{ marginRight: "8px", accentColor: "#000" }} 
                                />
                                <div style={{ lineHeight: "1.7" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                    <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
             </div>
           )}
        </div>

        {/* INPUTS */}
        
        {/* FIXED HAND SELECTION */}
        <SegmentedControl 
          label="Dominant Hand" 
          options={holsterData.hands} 
          selectedValue={handId} 
          onChange={(val) => {
              setHandId(val);
              setGunId(""); // RESET GUN ON HAND CHANGE
          }} 
        />
        
        <OptionSelector label="Select Manufacturer *" options={holsterData.brands} selectedValue={brandId} onChange={(val) => { setBrandId(val); setGunId(""); }} />
        <OptionSelector label="Gun Model *" options={availableGuns} selectedValue={gunId} onChange={setGunId} disabled={!brandId} />

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        {/* COLORS */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Color Options</h3>
        </div>
        <ColorSelector options={holsterData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* MOUNTING */}
        <h3 style={{ textTransform: "uppercase" }}>Mounting Options</h3>
        <p style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>
          Includes DCC-MOD 4 Universal Belt Clip.
        </p>
        <OptionSelector label="Select Clip Size *" options={holsterData.mounts} selectedValue={mountId} onChange={setMountId} />

        {/* WARRANTY */}
        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Lifetime Warranty</h3>
        <SegmentedControl label="" options={holsterData.warranties} selectedValue={warrantyId} onChange={setWarrantyId} />

        {/* BREAKTHROUGH */}
        <div style={{ marginTop: "30px", marginBottom: "15px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Add Breakthrough Clean</h3>
           <div style={{ 
               backgroundColor: "#1e1e1e", 
               borderLeft: "2px solid #aaa", 
               padding: "10px", 
               marginTop: "10px", 
               fontSize: "13px", 
               color: "#ccc", 
               lineHeight: "1.5" 
           }}>
             <strong>Optional Add-On:</strong> Premium firearm cleaning solvents and lubricants. <br/>
             <em>Not required for the holster itself.</em>
           </div>
        </div>
        <OptionSelector label="Optional firearm maintenance" options={holsterData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        <hr style={{margin:"50px 0", borderTop:"1px solid #333"}}/>

        {/* DESCRIPTION */}
        <div style={{ marginBottom: "40px", color: "#aaa", lineHeight: "1.6" }}>
          <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Description</h3>
          <p>
            The <strong>Cradle</strong> is a minimalist KYDEX® Thermoplastics holster designed for comfortable 
            and secure concealed carry, even with a suppressor attached. This ambidextrous holster 
            utilizes the trigger guard and slide profile for retention, offering a low-profile solution 
            for carrying your suppressed firearm.
          </p>
          <h4 style={{ color: "#D5A678", marginTop: "20px" }}>Benefits</h4>
          <ul style={{ paddingLeft: "20px" }}>
            <li><strong>Enhanced Concealment:</strong> Carry your suppressed firearm discreetly.</li>
            <li><strong>Comfortable Carry:</strong> Minimalist design and appendix position provide all-day comfort.</li>
            <li><strong>Secure Retention:</strong> Keeps your firearm secure while allowing for a smooth draw.</li>
          </ul>
        </div>
      </div>

      {/* PREVIEW SECTION */}
      <div className="preview-section">
        <div className="sticky-wrapper">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          <HolsterVisualizer 
            holsterType="cradle" 
            gunData={selectedGun} 
            colorData={selectedColor} 
            mountData={selectedMount} 
            isLightbearing="no"
            isLeftHanded={handId === "left"} 
            viewSide={viewSide} 
          />
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>
                    ${subtotal.toFixed(2)}
                </span>
                <span style={{ color: "#00ff00" }}>
                    ${total}
                </span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>
          
          {/* LEAD TIME INDICATOR */}
          {leadTimeInfo.type !== "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "10px", padding: "10px", border: `1px solid ${leadTimeInfo.color}`, borderRadius: "4px", color: leadTimeInfo.color }}>
              {leadTimeInfo.type && <div style={{ fontSize: "11px", fontWeight: "bold" }}>{leadTimeInfo.type}</div>}
              <div style={{ fontSize: "14px", color: "#fff" }}>Ships by {leadTimeInfo.date}</div>
            </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "10px", color: "#666", fontSize: "12px" }}>Select options to see lead time</div>
          )}

          <button className="share-build-btn" onClick={() => setShareModalOpen(true)} disabled={!gunId}>Save & Share Build</button>

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
            {isReady ? "ADD TO CART" : "SELECT OPTIONS"}
          </button>
        </div>
      </div>
      
      {/* MOBILE BAR */}
      <div className="mobile-action-bar">
        <div className="mobile-price-container">
          <span className="mobile-label">Estimated Total</span>
          <span className="mobile-total">${total}</span>
        </div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
           {isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareableURL={shareableURL}
        currentConfig={getCurrentConfig()}
        savedConfigs={savedConfigs}
        onSaveConfig={saveNamedConfig}
        onLoadConfig={loadConfig}
        onDeleteConfig={deleteNamedConfig}
        onCopyLink={copyToClipboard}
        copySuccess={copySuccess}
        gunName={selectedGun?.name}
        colorName={selectedColor?.name}
      />
    </div>
  );
}

export default CradleBuilder;